/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ChevronDown, Clock, ArrowRight, Newspaper, Users } from "lucide-react";
import api from "@/lib/api";

/**
 * Landing-page expansion pack (D.1 – D.6).
 *
 * Separated into one file so we don't bloat Landing.jsx. Includes:
 *   • NotForProfitBanner — compact ribbon under the hero that states KB's
 *     not-for-profit mission.
 *   • ResearchAreaCards — grouped by broad category, each card expands on
 *     click to show sub-areas, current work and estimated time-to-publication.
 *     The card CTA links to /get-help so interested leads are captured with
 *     zero friction (no login needed).
 *   • MentorHighlights — thin horizontally-scrollable mentor profile strip.
 *   • ImageCarousel — simple auto-rotating hero carousel driven by the
 *     existing /brand/landing-slots assets, with manual next/prev.
 *   • LatestNewsStrip — latest 3 published blog posts.
 */

export function NotForProfitBanner() {
  // D.3 — WhatsApp inquiry survey: prewritten greeting that links directly
  // to the no-login /get-help form. Visitors tap WhatsApp, send the message,
  // and we respond with the form link so leads convert without friction.
  const waText = encodeURIComponent(
    "Hi KhojByte! I’m interested in getting support with my research. Could you tell me more about the research services you offer?"
  );
  const waHref = `https://wa.me/9779851423235?text=${waText}`;
  return (
    <div className="bg-emerald-50 border-y border-emerald-100 py-3" data-testid="landing-nonprofit-banner">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-3 text-sm">
        <Heart className="w-4 h-4 text-emerald-700" />
        <span className="text-emerald-900 font-medium">KhojByte is a <strong>multidisciplinary research lab</strong></span>
        <span className="text-emerald-800/80">
          — we explore, develop and share research across disciplines: connecting researchers, incubating ideas, and advancing knowledge from spark to publication.
        </span>
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer noopener"
          className="kb-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
          data-testid="landing-whatsapp-survey-link"
        >
          Message us on WhatsApp
        </a>
      </div>
    </div>
  );
}

const RESEARCH_DOMAINS = [
  {
    key: "cs", name: "Computer Science & AI", accent: "indigo",
    tagline: "From language models to the edge.",
    time: "6-12 months",
    subs: [
      { k: "Artificial Intelligence", areas: ["Large language models", "Retrieval-augmented generation", "Nepali-language NLP", "Vision & multimodal"] },
      { k: "Machine Learning", areas: ["Health diagnostics", "Agricultural yield prediction", "Fraud & misinformation detection"] },
      { k: "Systems & Networks", areas: ["Programmable rural networking", "Edge & IoT deployments", "SDN testbeds"] },
      { k: "Cybersecurity", areas: ["Phishing & misinformation", "IoT device hardening", "Privacy-preserving ML"] },
    ],
  },
  {
    key: "env", name: "Climate, Environment & Energy", accent: "emerald",
    tagline: "Monsoon, glaciers, air quality, renewables.",
    time: "8-14 months",
    subs: [
      { k: "Climate Change & Disaster Mgmt", areas: ["Flood & landslide early warning", "Local-adaptation policy evidence", "Loss & damage attribution"] },
      { k: "Environment & Ecology", areas: ["Air-quality (PM2.5) sensing", "Water resources & glacier melt", "Biodiversity monitoring"] },
      { k: "Energy", areas: ["Micro-hydro optimisation", "Rural solar + storage", "Clean-cooking transitions"] },
    ],
  },
  {
    key: "med", name: "Medicine, Public Health & Life Sciences", accent: "rose",
    tagline: "Clinical trials, epidemiology, mountain health.",
    time: "9-15 months",
    subs: [
      { k: "Medicine & Clinical Research", areas: ["High-altitude cardiology", "Infectious disease trials", "Maternal & child health"] },
      { k: "Public Health", areas: ["Dengue outbreak modelling", "Nutrition surveillance", "Mental-health & stigma studies"] },
      { k: "Biotechnology & Chemistry", areas: ["Plant-based biologics", "Fermentation & food biotech", "Green chemistry"] },
      { k: "Digital Health", areas: ["mHealth adherence (TB, HIV)", "AI-assisted triage", "Wearable analytics"] },
    ],
  },
  {
    key: "agri", name: "Agriculture, Food & Water", accent: "amber",
    tagline: "From Terai paddy to Himalayan apple.",
    time: "6-12 months",
    subs: [
      { k: "Agriculture", areas: ["Precision farming for smallholders", "Climate-resilient varieties", "Post-harvest loss"] },
      { k: "Food Systems", areas: ["Supply-chain traceability", "Nutrition-sensitive agriculture"] },
      { k: "Water & Hydrology", areas: ["Smallholder irrigation", "Watershed restoration"] },
    ],
  },
  {
    key: "soc", name: "Social Sciences & Economics", accent: "sky",
    tagline: "Governance, remittances, learning outcomes.",
    time: "6-10 months",
    subs: [
      { k: "Economics", areas: ["Remittance dependence", "Informal-sector productivity", "Micro-insurance"] },
      { k: "Public Policy & Governance", areas: ["Citizen trust in federalism", "Fiscal decentralisation", "Civic-tech evaluations"] },
      { k: "Gender & Development", areas: ["Workforce participation", "Post-conflict reintegration", "Access to finance"] },
    ],
  },
  {
    key: "edu", name: "Education & Interdisciplinary", accent: "violet",
    tagline: "Learning, pedagogy, new disciplinary mash-ups.",
    time: "4-8 months",
    subs: [
      { k: "Education", areas: ["Hybrid-instruction outcomes", "Teacher professional development", "EdTech evaluations"] },
      { k: "STEM Pedagogy", areas: ["Open-source curricula", "Low-resource lab kits"] },
      { k: "Interdisciplinary", areas: ["Climate & health", "AI & governance", "Energy & gender"] },
    ],
  },
];

const ACCENT_CLASSES = {
  indigo:  { ring: "ring-indigo-200",  bar: "bg-indigo-500",  dot: "bg-indigo-500",  chip: "bg-indigo-50 text-indigo-700 border-indigo-200",  grad: "from-indigo-500/10" },
  emerald: { ring: "ring-emerald-200", bar: "bg-emerald-500", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", grad: "from-emerald-500/10" },
  rose:    { ring: "ring-rose-200",    bar: "bg-rose-500",    dot: "bg-rose-500",    chip: "bg-rose-50 text-rose-700 border-rose-200",       grad: "from-rose-500/10" },
  amber:   { ring: "ring-amber-200",   bar: "bg-amber-500",   dot: "bg-amber-500",   chip: "bg-amber-50 text-amber-800 border-amber-200",     grad: "from-amber-500/10" },
  sky:     { ring: "ring-sky-200",     bar: "bg-sky-500",     dot: "bg-sky-500",     chip: "bg-sky-50 text-sky-700 border-sky-200",           grad: "from-sky-500/10" },
  violet:  { ring: "ring-violet-200",  bar: "bg-violet-500",  dot: "bg-violet-500",  chip: "bg-violet-50 text-violet-700 border-violet-200",  grad: "from-violet-500/10" },
};

/** Build a tree { key,name,accent,tagline,time,id, subs:[{k,id, areas:[{name,id}]}] }
 *  from the flat `/api/taxonomy?domain=landing` response. Falls back to the
 *  hardcoded `RESEARCH_DOMAINS` list when the DB hasn't been seeded yet (so
 *  initial render never flashes empty). */
function _buildLandingTree(flatNodes) {
  if (!flatNodes || flatNodes.length === 0) {
    return RESEARCH_DOMAINS.map((d) => ({
      ...d, id: null,
      subs: d.subs.map((s) => ({
        k: s.k, id: null,
        areas: (s.areas || []).map((a) => ({ name: a, id: null })),
      })),
    }));
  }
  const byParent = new Map();
  for (const n of flatNodes) {
    const key = n.parent_id || "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(n);
  }
  const sortNodes = (arr) => arr.slice().sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
  const roots = sortNodes(byParent.get("__root__") || []);
  const accents = ["indigo", "emerald", "rose", "amber", "sky", "violet"];
  return roots.map((r, i) => {
    const subs = sortNodes(byParent.get(r.id) || []).map((s) => ({
      k: s.name, id: s.id,
      areas: sortNodes(byParent.get(s.id) || []).map((a) => ({ name: a.name, id: a.id })),
    }));
    return {
      key: r.slug || r.id, name: r.name, id: r.id,
      accent: r.accent || accents[i % accents.length],
      tagline: r.tagline || "",
      time: r.time || "",
      subs,
    };
  });
}

export function ResearchAreaCards() {
  const [domains, setDomains] = useState(() => _buildLandingTree(null));
  const [openKey, setOpenKey] = useState(null);
  const [subIdx, setSubIdx] = useState(0);
  const [areaIdx, setAreaIdx] = useState(-1);

  useEffect(() => {
    let active = true;
    api.get("/taxonomy")
      .then((r) => { if (active && r?.data?.length) setDomains(_buildLandingTree(r.data)); })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
  }, []);

  const open = domains.find((d) => d.key === openKey);
  const acc = ACCENT_CLASSES[open?.accent || "indigo"];
  const subs = open?.subs || [];
  const sub = subs[subIdx];
  const area = sub?.areas?.[areaIdx];
  // Deepest selected node — pass its DB id to the related content strip so
  // the drill-down filters by node + descendants.
  const selectedNodeId = area?.id || sub?.id || open?.id || null;
  const sel = {
    domain: open?.name,
    sub: sub?.k,
    area: typeof area === "string" ? area : area?.name,
    nodeId: selectedNodeId,
  };

  return (
    <section id="research" className="py-20 lg:py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="landing-research-areas">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2">05 · RESEARCH AT KHOJBYTE</div>
          <h2 className="font-serif text-3xl lg:text-5xl text-kb-ink leading-tight">
            {domains.length || "Several"} domain{domains.length === 1 ? "" : "s"}. Dozens of <em className="italic text-kb-primary">open problems.</em>
          </h2>
          <p className="text-sm text-kb-ink-muted mt-3 max-w-2xl">
            Explore the research we actively develop and support. Click any domain to drill down — <strong>Domain → Subdomain → Specific area</strong> — then jump straight to papers, ideas and open problems in that thread.
          </p>
        </div>
      </div>

      {/* Domain cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {domains.map((d, i) => {
          const a = ACCENT_CLASSES[d.accent];
          const isOpen = openKey === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => { setOpenKey(isOpen ? null : d.key); setSubIdx(0); setAreaIdx(-1); }}
              className={`group relative kb-card p-6 text-left overflow-hidden transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md ${isOpen ? `ring-2 ${a.ring} shadow-sm` : ""}`}
              style={{ animation: `kbFadeUp 500ms ${i * 60}ms both` }}
              data-testid={`research-domain-${d.key}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${a.grad} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
              <div className="relative flex items-start gap-3">
                <span className={`mt-1 w-2 h-2 rounded-full ${a.dot}`} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-kb-ink leading-tight">{d.name}</div>
                  <div className="text-xs text-kb-ink-muted mt-1">{d.tagline}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mt-3 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {d.time} to publish · {d.subs.length} subdomain{d.subs.length === 1 ? "" : "s"}
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-kb-ink-muted shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Drill-down panel */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${open ? "max-h-[2000px] opacity-100 mt-6" : "max-h-0 opacity-0"}`}
        aria-hidden={!open}
      >
        {open && (
          <div className={`kb-card p-6 lg:p-8 ring-1 ${acc.ring}`} data-testid="research-drilldown">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-block w-1 h-6 rounded ${acc.bar}`} aria-hidden="true" />
              <div className="font-serif text-2xl text-kb-ink">{open.name}</div>
              <span className={`kb-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${acc.chip}`}>{open.time}</span>
            </div>

            {/* Subdomain chips (second level) */}
            <div className="mt-5">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">SUBDOMAIN</div>
              <div className="flex flex-wrap gap-2">
                {subs.map((s, i) => {
                  const on = subIdx === i;
                  return (
                    <button
                      key={s.k}
                      type="button"
                      onClick={() => { setSubIdx(i); setAreaIdx(-1); }}
                      className={`px-3 py-1.5 rounded-sm border text-xs transition-all duration-200 ${on ? `${acc.chip} font-medium scale-[1.02]` : "border-slate-200 text-kb-ink-muted hover:border-kb-primary hover:text-kb-ink"}`}
                      data-testid={`research-sub-${open.key}-${i}`}
                    >
                      {s.k}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Specific areas (third level) */}
            {sub && (
              <div className="mt-5">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">SPECIFIC AREA</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {sub.areas.map((a, i) => {
                    const on = areaIdx === i;
                    const label = typeof a === "string" ? a : a.name;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setAreaIdx(on ? -1 : i)}
                        className={`text-left px-3 py-2 rounded-sm border transition-all duration-200 ${on ? `${acc.chip} font-medium` : "border-slate-200 bg-white hover:border-kb-primary"}`}
                        style={{ animation: `kbFadeUp 300ms ${i * 40}ms both` }}
                        data-testid={`research-area-${open.key}-${subIdx}-${i}`}
                      >
                        <div className="text-sm text-kb-ink">{label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Related content strip (fourth level) */}
            <RelatedResearchStrip selected={sel} accent={acc} />
          </div>
        )}
      </div>

      {/* Scoped keyframes — kept inline so Landing.jsx stays unchanged */}
      <style>{`
        @keyframes kbFadeUp {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

/** Bottom strip of real content tied to the current drill-down selection.
 *  Pulls lightweight public endpoints — falls back gracefully to a cheerful
 *  empty state if an endpoint doesn't exist or returns nothing. */
function RelatedResearchStrip({ selected, accent }) {
  const [content, setContent] = React.useState({
    published: [], working: [], curated: [], areas: [], problems: [], loading: true,
  });
  const q = (selected.area || selected.sub || selected.domain || "").trim();
  const nodeId = selected.nodeId || null;
  React.useEffect(() => {
    let active = true;
    setContent((c) => ({ ...c, loading: true }));
    // When a DB taxonomy node is selected we filter server-side by node +
    // descendants. We still pass `q` so legacy items (not yet tagged with
    // taxonomy_node_id) can still match by text — belt + braces.
    const paramsBase = { q, limit: 4 };
    if (nodeId) paramsBase.taxonomy_node_id = nodeId;
    const paramsSix = { ...paramsBase, limit: 6 };
    const tries = [
      api.get("/papers", { params: paramsBase }).catch(() => ({ data: [] })),
      api.get("/ideas/public", { params: { ...paramsBase, status: "in_progress" } }).catch(() => ({ data: [] })),
      api.get("/ideas/public", { params: { ...paramsBase, status: "curated" } }).catch(() => ({ data: [] })),
      api.get("/research-areas", { params: paramsSix }).catch(() => ({ data: [] })),
      api.get("/open-problems", { params: paramsSix }).catch(() => ({ data: [] })),
    ];
    Promise.all(tries).then(([pub, wip, cur, ar, prob]) => {
      if (!active) return;
      setContent({
        published: pub?.data || [], working: wip?.data || [], curated: cur?.data || [],
        areas: ar?.data || [], problems: prob?.data || [], loading: false,
      });
    });
    return () => { active = false; };
  }, [q, nodeId]);

  const strips = [
    { label: "Published Papers", key: "published", testId: "related-published" },
    { label: "Working Ideas", key: "working", testId: "related-working" },
    { label: "Curated Ideas", key: "curated", testId: "related-curated" },
    { label: "Possible Research Areas", key: "areas", testId: "related-areas" },
    { label: "Open Research Problems", key: "problems", testId: "related-problems" },
  ];

  return (
    <div className="mt-8 pt-6 border-t border-slate-200">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RELATED · {q || "ANY"}</div>
        <Link to={`/get-help?area=${encodeURIComponent(q)}`} className={`kb-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border ${accent.chip} hover:opacity-80`}>
          Start a project <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {strips.map((s) => {
          const rows = content[s.key] || [];
          return (
            <div key={s.key} className="kb-card p-4 bg-white" data-testid={s.testId}>
              <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary mb-2">{s.label}</div>
              {content.loading && <div className="text-[11px] text-kb-ink-light italic">Loading…</div>}
              {!content.loading && rows.length === 0 && <div className="text-[11px] text-kb-ink-light italic">No matches yet — be the first.</div>}
              <ul className="space-y-2 text-sm">
                {rows.slice(0, 4).map((r, i) => {
                  const title = r.title || r.name || r.question || r.area || "Untitled";
                  const href = r.url || r.link || r.doi || "";
                  const body = (
                    <>
                      <div className="text-xs font-medium text-kb-ink line-clamp-2">{title}</div>
                      {r.year || r.domain || r.priority ? (
                        <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted mt-0.5">{[r.year, r.domain, r.priority].filter(Boolean).join(" · ")}</div>
                      ) : null}
                    </>
                  );
                  return (
                    <li key={r.id || r.slug || i} className="border-b border-slate-100 last:border-b-0 pb-2 last:pb-0">
                      {href ? <a href={href} target="_blank" rel="noreferrer" className="hover:text-kb-primary block">{body}</a> : body}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MentorHighlights() {
  const [mentors, setMentors] = useState([]);
  const [activeDomain, setActiveDomain] = useState("All");
  useEffect(() => {
    api.get("/mentors").then((r) => setMentors(r.data || [])).catch(() => {});
  }, []);
  if (!mentors.length) return null;

  const domains = Array.from(new Set(mentors.flatMap((m) => m.domains || []))).sort();
  const shown = (activeDomain === "All" ? mentors : mentors.filter((m) => (m.domains || []).includes(activeDomain))).slice(0, 8);

  return (
    <section className="bg-white border-y border-slate-200 py-20" data-testid="landing-mentors">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2 inline-flex items-center gap-1"><Users className="w-3 h-3" /> MENTOR NETWORK</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-kb-ink leading-tight">Meet the mentors.</h2>
          </div>
          <Link to="/mentors" className="kb-btn-outline text-sm">All mentors <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>

        {domains.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="landing-mentor-domain-selector">
            <button
              type="button"
              onClick={() => setActiveDomain("All")}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${activeDomain === "All" ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 text-kb-ink-muted hover:border-kb-primary hover:text-kb-primary"}`}
              data-testid="landing-mentor-domain-all"
            >
              All
            </button>
            {domains.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDomain(d)}
                className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${activeDomain === d ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 text-kb-ink-muted hover:border-kb-primary hover:text-kb-primary"}`}
                data-testid={`landing-mentor-domain-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {shown.map((m) => {
            const profileHref = `/mentors/${m.slug || m.id}`;
            return (
              <div key={m.id} className="kb-card p-5 hover:border-kb-primary/40 transition-colors flex flex-col" data-testid={`landing-mentor-${m.id}`}>
                <Link to={profileHref} className="flex items-start gap-3">
                  <img
                    src={m.photo_url || `https://ui-avatars.com/api/?background=random&name=${encodeURIComponent(m.name || "?")}`}
                    alt={m.name}
                    className="w-14 h-14 rounded-full object-cover border border-slate-200 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="font-serif text-base text-kb-ink truncate">{m.title ? `${m.title} ` : ""}{m.name}</div>
                    <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted mt-0.5 truncate">{m.affiliation}</div>
                    <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary mt-1 truncate">
                      {(m.expertise || m.domains || []).slice(0, 2).join(" · ")}
                    </div>
                  </div>
                </Link>
                {m.bio && <p className="text-xs text-kb-ink-muted mt-3 line-clamp-2">{m.bio}</p>}
                <div className="mt-3 flex items-center gap-3 text-[10px] kb-mono text-kb-ink-light">
                  {typeof m.h_index === "number" && m.h_index > 0 && <span>h-index {m.h_index}</span>}
                  {typeof m.publications_count === "number" && m.publications_count > 0 && <span>{m.publications_count} pubs</span>}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200 flex gap-2">
                  <Link to={profileHref} className="flex-1 text-center kb-mono text-[10px] uppercase tracking-wider px-2 py-1.5 rounded-sm border border-slate-200 text-kb-ink-muted hover:border-kb-primary hover:text-kb-primary transition-colors" data-testid={`landing-mentor-${m.id}-portfolio`}>
                    Portfolio
                  </Link>
                  <Link to={`/dashboard/guidance/new?mentor_id=${m.id}`} className="flex-1 text-center kb-mono text-[10px] uppercase tracking-wider px-2 py-1.5 rounded-sm bg-kb-primary text-white hover:opacity-90 transition-opacity" data-testid={`landing-mentor-${m.id}-book`}>
                    Book time
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ImageCarousel() {
  const [slots, setSlots] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    api.get("/brand/landing-slots").then((r) => {
      const items = (r.data || []).filter((s) => s.image_url).slice(0, 6);
      setSlots(items);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (slots.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slots.length), 5000);
    return () => clearInterval(t);
  }, [slots.length]);
  if (slots.length === 0) return null;
  const slot = slots[idx];
  return (
    <section className="bg-kb-dark text-white relative overflow-hidden" data-testid="landing-carousel">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mb-3">07 · OUR WORK IN PICTURES</div>
          <h2 className="font-serif text-3xl lg:text-5xl leading-tight">{slot.caption || "A snapshot of KhojByte."}</h2>
          <p className="text-white/70 text-sm mt-3">{slot.description || "Our mentors, researchers and partner institutions in action."}</p>
          <div className="mt-6 flex gap-2">
            {slots.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-white" : "w-5 bg-white/30 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="relative aspect-[16/10] rounded-sm overflow-hidden border border-white/10">
            <img src={slot.image_url} alt={slot.caption || ""} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function LatestNewsStrip() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blogs", { params: { limit: 3 } }).then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  if (posts.length === 0) return null;
  return (
    <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10" data-testid="landing-news">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2 inline-flex items-center gap-1"><Newspaper className="w-3 h-3" /> NEWS & JOURNAL</div>
          <h2 className="font-serif text-3xl lg:text-5xl text-kb-ink leading-tight">Latest from our journal.</h2>
        </div>
        <Link to="/blogs" className="kb-btn-outline text-sm">Read more <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {posts.map((p) => (
          <Link key={p.id || p.slug} to={`/blogs/${p.slug}`} className="kb-card overflow-hidden hover:border-kb-primary/40 transition-colors" data-testid={`landing-news-${p.slug}`}>
            {p.cover_image_url && (
              <div className="aspect-[16/9] overflow-hidden"><img src={p.cover_image_url} alt="" className="w-full h-full object-cover" /></div>
            )}
            <div className="p-5">
              <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary">{p.domain || "RESEARCH"} · {p.read_minutes || 5} MIN</div>
              <h3 className="font-serif text-lg text-kb-ink mt-2 line-clamp-2">{p.title}</h3>
              {p.excerpt && <p className="text-xs text-kb-ink-muted mt-2 line-clamp-3">{p.excerpt}</p>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
